import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toNumberOrNull, toIntOr, formatUSD } from "./formatters";



export default function useInventoryGrid({
  inventory,
  isQuickEdit,
  onThumbnailClick,
  onViewChangeHistory,
  sortPersistenceKey,
  categoryOptions = [],
}) {
  
 
  // ---- STATE MOVED FROM SalesInv.jsx ----
  const [pageRows, setPageRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [hasColumnChanges, setHasColumnChanges] = useState(false);
  const [lastPointerType, setLastPointerType] = useState(null);
  const [sortModel, setSortModel] = useState(() => {
    if (!sortPersistenceKey) return [];
    try {
      const stored = sessionStorage.getItem(sortPersistenceKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [gridReady, setGridReady] = useState(false);

  // ---- GRID REFS ----
  const gridRef = useRef(null);
  const initialColumnState = useRef(null);
  const quickEditColumnState = useRef(null);
  const isApplyingColumnStateRef = useRef(false);
  const sortModelRef = useRef(sortModel);

  useEffect(() => {
    sortModelRef.current = sortModel;
  }, [sortModel]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (event?.pointerType) {
        setLastPointerType(event.pointerType);
      }
    };
    const handleTouchStart = () => {
      setLastPointerType('touch');
    };
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
  const shouldDisableSelectAll = isTouchDevice || lastPointerType === 'touch';

  const applySortModel = useCallback((model) => {
    if (!model || model.length === 0) {
      return;
    }
    const api = gridRef.current?.api;
    if (!api || typeof api.applyColumnState !== 'function') {
      return;
    }
    isApplyingColumnStateRef.current = true;
    api.applyColumnState({
      state: model,
      applyOrder: false,
    });
    setTimeout(() => {
      isApplyingColumnStateRef.current = false;
    }, 0);
  }, []);

  const normalizeColumnState = useCallback((state) => {
    return (state || []).map((col) => ({
      colId: col.colId,
      hide: !!col.hide,
      pinned: col.pinned || null,
    }));
  }, []);

  const computeHasColumnChanges = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !initialColumnState.current) {
      return false;
    }
    const current = normalizeColumnState(api.getColumnState());
    const initial = normalizeColumnState(initialColumnState.current);
    if (current.length !== initial.length) return true;
    for (let i = 0; i < current.length; i += 1) {
      const a = current[i];
      const b = initial[i];
      if (
        a.colId !== b.colId ||
        a.hide !== b.hide ||
        a.pinned !== b.pinned
      ) {
        return true;
      }
    }
    return false;
  }, [normalizeColumnState]);

  const refreshHasColumnChanges = useCallback(() => {
    setHasColumnChanges(computeHasColumnChanges());
  }, [computeHasColumnChanges]);
// ---------------------------------------------------------------------------
//  Grid Column Definitions (exact from original salesinvorig.jsx, adapted thumbnail click)
// ---------------------------------------------------------------------------
const normalizedCategoryOptions = useMemo(() => {
  const seen = new Set();
  const values = [];
  for (const raw of categoryOptions || []) {
    const value = String(raw || '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    values.push(value);
  }
  return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}, [categoryOptions]);

const normalColumnDefs = useMemo(() => {
  const touchSelectAllParam = shouldDisableSelectAll ? { selectAllOnFocus: false } : {};
  return [
   
  {
    field: 'url',
    headerName: 'Thumbnail',
    width: 120,
    sortable: false,
    cellStyle: { textAlign: 'left' },
    cellRenderer: (params) =>
      params.value && params.value !== 'N/A' ? (
        <img
          src={params.value}
          alt="item thumbnail"
          style={{ width: '100px', height: 'auto', objectFit: 'cover', cursor: 'pointer' }}
          onClick={() => {
            if (params.context?.onThumbnailClick) {
              params.context.onThumbnailClick(params.data);
            }
          }}
          onError={(e) => console.log('Image load error for URL:', e.target.src)}
          loading="lazy"
        />
      ) : (
        'N/A'
      ),
  },
  // 1) Title
  {
    field: 'title',
    headerName: 'Title',
    flex: 0.7,
    minWidth: 140,
    maxWidth: 420,
    suppressSizeToFit: true,
    sortable: true,
    editable: true,
    wrapText: true,
    autoHeight: true,
    cellStyle: (params) => ({
      whiteSpace: 'normal',
      fontSize: '1.1em',
      ...(params.data.isTitleEdited && {
        color: 'rgb(59, 130, 246)',
        fontWeight: 'bold',
      }),
    }),
    cellEditor: 'agLargeTextCellEditor',
    cellEditorParams: {
      useFormatter: false,
      selectAllOnFocus: false,
      wrapText: true,
      rows: 3,
      cols: 20,
      resize: 'vertical',
    },
    valueParser: (p) => String(p.newValue ?? '').trim(),
  },
  // 2) Price
  {
    field: 'per_item_cost_estimate',
    headerName: 'Price',
    headerClass: 'center-header',
    width: 110,
    minWidth: 96,
    sortable: true,
    editable: true,
    cellStyle: (params) => ({
      textAlign: 'center',
      fontVariantNumeric: 'tabular-nums',
      ...(params.data.isPriceEdited && {
        color: 'rgb(59, 130, 246)',
        fontWeight: 'bold',
      }),
    }),
    cellEditor: 'agNumberCellEditor',
    cellEditorParams: { valueIsNumeric: true, step: 1, min: 0, selectAllOnFocus: false, ...touchSelectAllParam },
    valueParser: (p) => {
      if (p.newValue === '' || p.newValue == null) {
        return p.oldValue;
      }
      return toNumberOrNull(p.newValue);
    },
    valueFormatter: (p) => formatUSD(p.value),
  },
  // 3) Quantity
  {
    field: 'quantity_estimate',
    headerName: 'Quantity',
    headerClass: 'center-header',
    width: 90,
    sortable: true,
    editable: true,
    cellEditor: 'agNumberCellEditor',
    cellEditorParams: { min: 1, valueIsNumeric: true, step: 1, ...touchSelectAllParam },
    valueParser: (p) => toIntOr(p.newValue, 1),
    valueFormatter: (p) => String(toIntOr(p.value, 1)),
    cellStyle: (params) => ({
      textAlign: 'center',
      fontVariantNumeric: 'tabular-nums',
      ...(params.data.isQtyEdited && {
        color: 'rgb(59, 130, 246)',
        fontWeight: 'bold',
      }),
    }),
  },
  // 4) Changes
  {
    field: 'changes',
    headerName: 'History',
    width: 100,
    sortable: false,
    editable: false,
    cellRenderer: (params) => {
      const hasChanges = Array.isArray(params.data?.recentEdits) && params.data.recentEdits.length > 0;
      if (!hasChanges) return '';
      return (
        <button
          type="button"
          className="text-blue-600 underline text-xs"
          onClick={() => {
            if (params.context?.onViewChangeHistory) {
              params.context.onViewChangeHistory(params.data);
            }
          }}
        >
          Show Changes
        </button>
      );
    },
  },
  // 5) Description
  {
    field: 'description',
    headerName: 'Description',
    flex: 1.2,
    minWidth: 200,
    sortable: false,
    editable: true,
    wrapText: true,
    autoHeight: true,
    cellStyle: { whiteSpace: 'normal', fontSize: '1.1em' },
    cellEditor: 'agLargeTextCellEditor',
    cellEditorParams: {
      useFormatter: false,
      selectAllOnFocus: false,
      wrapText: true,
      rows: 3,
      resize: 'vertical',
    },
    valueParser: (p) => String(p.newValue ?? '').trim(),
    tooltipField: 'description',
  },
  // Remaining columns unchanged
{
  field: 'resale_positioning',
  headerName: 'Resale Description',
  flex: 1,
  minWidth: 200,
  sortable: false,
  editable: true,
  wrapText: true,
  autoHeight: true,
  cellStyle: { whiteSpace: 'normal', fontSize: '1.1em' },
  cellEditor: 'agLargeTextCellEditor',
  cellEditorParams: {
    useFormatter: false,
    selectAllOnFocus: false,
    wrapText: true,
    rows: 3,
    resize: 'vertical',
  },
  // ADD THESE
  valueGetter: (params) => Array.isArray(params.data.resale_positioning) 
    ? params.data.resale_positioning.join('\n') 
    : params.data.resale_positioning || '',
  valueSetter: (params) => {
    if (params.newValue !== params.oldValue) {
      params.data.resale_positioning = params.newValue
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      return true;
    }
    return false;
  },
},
{
  field: 'pricing_context',
  headerName: 'Pricing Context',
  flex: 1,
  minWidth: 200,
  sortable: false,
  editable: true,
  wrapText: true,
  autoHeight: true,
  cellStyle: { whiteSpace: 'normal', fontSize: '1.1em' },
  cellEditor: 'agLargeTextCellEditor',
  cellEditorParams: {
    useFormatter: false,
    selectAllOnFocus: false,
    wrapText: true,
    rows: 3,
    resize: 'vertical',
  },
  // ADD THESE
  valueGetter: (params) => Array.isArray(params.data.pricing_context) 
    ? params.data.pricing_context.join('\n') 
    : params.data.pricing_context || '',
  valueSetter: (params) => {
    if (params.newValue !== params.oldValue) {
      params.data.pricing_context = params.newValue
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      return true;
    }
    return false;
  },
},
  {
    field: 'category',
    headerName: 'Category',
    width: 190,
    minWidth: 150,
    sortable: true,
    editable: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: (params) => {
      const current = String(params?.value || '').trim();
      const values = [...normalizedCategoryOptions];
      if (current && !values.some((value) => value.toLowerCase() === current.toLowerCase())) {
        values.unshift(current);
      }
      return { values };
    },
    valueParser: (p) => String(p.newValue ?? '').trim(),
  },
  { field: 'filename', headerName: 'Filename', width: 150, sortable: true,},
];
}, [normalizedCategoryOptions, shouldDisableSelectAll]);

const quickColumnDefs = useMemo(() => {
  const touchSelectAllParam = shouldDisableSelectAll ? { selectAllOnFocus: false } : {};
  return [

  {
    field: "url",
    headerName: "Thumbnail",
    width: 120,
    sortable: false,
    cellRenderer: (params) => {
      if (!params.value || params.value === "N/A") return "N/A";
      return (
        <img
          src={params.value}
          alt="thumbnail"
          style={{
            width: "100px",
            height: "auto",
            objectFit: "cover",
            cursor: "pointer",
          }}
          onClick={() => {
            if (params.context?.onThumbnailClick) {
              params.context.onThumbnailClick(params.data);
            }
          }}
        />
      );
    },
  },
  {
    field: "title",
    headerName: 'Title',
    sortable: true,
    editable: true,
    width: 180,
    minWidth: 180,
    maxWidth: 320,
    wrapText: true,
    autoHeight: true,
    suppressSizeToFit: true,
    cellEditor: "agLargeTextCellEditor",
    cellEditorParams: {
      useFormatter: false,
      selectAllOnFocus: false,
      wrapText: true,
      rows: 3,
      cols: 20,
      resize: 'vertical',
    },
    valueParser: (p) => String(p.newValue ?? '').trim(),
    cellEditorPopup: true,
    cellStyle: (params) => ({
      whiteSpace: 'normal',
      fontSize: '1.1em',
      ...(params.data.isTitleEdited && {
        color: 'rgb(59, 130, 246)',
        fontWeight: 'bold',
      }),
    }),
  },
  {
    field: "per_item_cost_estimate",
    headerName: "Price",
    width: 85,
    minWidth: 85,
    maxWidth: 100,
    sortable: true,
    editable: true,
    suppressSizeToFit: true,
    valueFormatter: (p) => formatUSD(p.value),
    valueParser: (p) => {
      if (p.newValue === '' || p.newValue == null) {
        return p.oldValue;
      }
      return toNumberOrNull(p.newValue);
    },
    cellEditor: "agTextCellEditor",
    cellEditorParams: {
      useFormatter: true,
      decimal: 2,
      maxLength: 10,
      selectAllOnFocus: false,
      ...touchSelectAllParam,
    },
    cellStyle: (params) => ({
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      ...(params.data.isPriceEdited && {
        color: "rgb(59, 130, 246)",
        fontWeight: "bold",
      }),
    }),
  },
  {
    field: "quantity_estimate",
    headerName: "Qty",
    headerClass: "center-header",
    width: 65,
    minWidth: 65,
    maxWidth: 90,
    sortable: true,
    editable: true,
    suppressSizeToFit: true,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: { min: 1, valueIsNumeric: true, step: 1, ...touchSelectAllParam },
    valueParser: (p) => toIntOr(p.newValue, 1),
    valueFormatter: (p) => String(toIntOr(p.value, 1)),
    cellStyle: (params) => ({
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
      ...(params.data.isQtyEdited && {
        color: "rgb(59, 130, 246)",
        fontWeight: "bold",
      }),
    }),
  },

];
}, [shouldDisableSelectAll]);


  // --- Selection update ---
  const updateSelectionState = useCallback(() => {
    const count = gridRef.current?.api?.getSelectedRows()?.length || 0;
    setSelectedCount(count);
  }, []);

  const handleSelectionChanged = useCallback(() => {
    updateSelectionState();
  }, [updateSelectionState]);

  const handleRowClicked = useCallback(() => {
    updateSelectionState();
  }, [updateSelectionState]);

  // --- Quick search / filter ---
  const handleSearchChange = useCallback((e) => {
  const value = e.target.value || "";
  if (gridRef.current?.api) {
    // Optional debounce
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      gridRef.current.api.setGridOption('quickFilterText', value);
    }, 300);
  }
}, []);

  // --- Sorting ---
  const handleSortChanged = useCallback((event) => {
    const source = String(event?.source || '');
    const hasApi = !!event?.api;
    if (!hasApi || !gridReady) {
      return;
    }
    const columnState =
      typeof event.api.getColumnState === 'function'
        ? event.api.getColumnState()
        : event.columnApi?.getColumnState?.() || [];
    const newSortModel = columnState
      .filter((col) => col.sort === "asc" || col.sort === "desc")
      .map((col) => ({ colId: col.colId, sort: col.sort }));
    if (isApplyingColumnStateRef.current) {
      return;
    }
    if (source && !source.startsWith('ui')) {
      if (sortModelRef.current.length > 0 && newSortModel.length === 0) {
        return;
      }
    }
    setSortModel(newSortModel);
  }, [gridReady]);

  const clearSortModel = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || typeof api.getColumnState !== 'function') return;
    isApplyingColumnStateRef.current = true;
    const nextState = api.getColumnState().map((col) => ({
      colId: col.colId,
      sort: null,
      sortIndex: null,
    }));
    api.applyColumnState({ state: nextState, applyOrder: false });
    setSortModel([]);
    setTimeout(() => {
      isApplyingColumnStateRef.current = false;
    }, 0);
  }, []);
  const columnDefs = isQuickEdit ? quickColumnDefs : normalColumnDefs;
 
 // onGridReady — no deps on data
// ──────────────────────────────────────────────────────────────
  // 2. Grid Ready –  
  // ──────────────────────────────────────────────────────────────
const onGridReady = useCallback((params) => {

  // DO NOT touch columnApi or api here — they can be undefined
  // Just save the ref and mark ready
  setGridReady(true);

  // Delay everything until next tick when grid is fully ready
  setTimeout(() => {
  const api = params?.api;
  if (!api || typeof api.getColumnState !== 'function') {
    console.warn('Grid API not ready yet, skipping setup');
    return;
  }

  // Save initial state (keep this)
  initialColumnState.current = api.getColumnState();

  isApplyingColumnStateRef.current = true;
  params.api.applyColumnState({ state: [], applyOrder: true });
  setTimeout(() => {
    isApplyingColumnStateRef.current = false;
  }, 0);
  params.api.sizeColumnsToFit();
  params.api.deselectAll();

  applySortModel(sortModelRef.current);

  const displayedRows = [];
  api.forEachNodeAfterFilter((node) => {
    if (node.data) {
      displayedRows.push(node.data);
    }
  });
  setPageRows(displayedRows);
  refreshHasColumnChanges();
}, 300); // Increased to 300ms — gives grid time to settle
}, [applySortModel, refreshHasColumnChanges]); // No dependencies — run once

  const onFirstDataRendered = useCallback(() => {
  applySortModel(sortModelRef.current);
  }, [applySortModel]);
  // --- Reset Columns ---
  const resetColumns = useCallback(() => {
    const columnApi = gridRef.current?.columnApi || gridRef.current?.api;
    if (columnApi && initialColumnState.current) {
      columnApi.applyColumnState({
        state: initialColumnState.current,
        applyOrder: true,
      });
      gridRef.current.api.sizeColumnsToFit();
      gridRef.current.api.refreshHeader();
      setHasColumnChanges(false);
    }
  }, []);

  const handleCellEditingStopped = useCallback(async (event) => {
    const { colDef, data, newValue, oldValue } = event;
    if (!data || newValue == null) return;
    const field = colDef.field;
    try {
      if (field === "title") {
        await event.api.context.commitTitle(data.id, newValue);
      } else if (field === "per_item_cost_estimate") {
        if (newValue === '' || newValue === oldValue) return;
        await event.api.context.commitPrice(data.id, newValue);
      } else if (field === "quantity_estimate") {
        await event.api.context.commitQty(data.id, newValue);
      }
      else if (field === "resale_positioning") {
        await event.api.context.commitResalePositioning(data.id, newValue);
        } else if (field === "pricing_context") {
        await event.api.context.commitPricingContext(data.id, newValue);
        }
      event.api.refreshCells({ rowNodes: [event.node] });
    } catch (err) {
      console.error("Edit RPC error", err);
    }
  }, []);




 // ──────────────────────────────────────────────────────────────
// CRITICAL: Keep pageRows in sync when rowData prop updates the grid
// ──────────────────────────────────────────────────────────────

useEffect(() => {
  const updateRows = () => {
    const api = gridRef.current?.api;
    if (!api) {
      setPageRows([]); // Safe fallback during hot reload
      return;
    }

    const rows = [];
    api.forEachNodeAfterFilter((node) => {
      if (node.data) rows.push(node.data);
    });
    setPageRows(rows);
  };

  updateRows();
  const timer = setTimeout(updateRows, 100);
  return () => clearTimeout(timer);
}, [inventory]);
// Re-apply user sort after data refreshes
useEffect(() => {
  if (!gridReady || !sortModel.length) return;
  applySortModel(sortModel);
  const t1 = setTimeout(() => applySortModel(sortModel), 200);
  const t2 = setTimeout(() => applySortModel(sortModel), 800);
  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
  };
}, [inventory, gridReady, sortModel, applySortModel]);
// ──────────────────────────────────────────────────────────────
  // 3. Apply row data when inventory actually arrives
  // ──────────────────────────────────────────────────────────────
 useEffect(() => {
  if (!gridReady || !gridRef.current?.api) return;

  const rows = [];
  gridRef.current.api.forEachNodeAfterFilter((node) => {
    if (node.data) {
      rows.push(node.data);
    }
  });

  setPageRows(rows);
}, [inventory, gridReady]);
 // --- Column switching ONLY — NO saved state restore at all ---
useEffect(() => {
  if (!sortPersistenceKey) return;
  try {
    sessionStorage.setItem(sortPersistenceKey, JSON.stringify(sortModel));
  } catch {
  }
}, [sortModel, sortPersistenceKey]);

useEffect(() => {
  if (!sortPersistenceKey) return;
  try {
    const stored = sessionStorage.getItem(sortPersistenceKey);
    const next = stored ? JSON.parse(stored) : [];
    setSortModel(next);
  } catch {
    setSortModel([]);
  }
}, [sortPersistenceKey]);

useEffect(() => {
  const columnApi = gridRef.current?.columnApi;
  if (!columnApi) return;

  isApplyingColumnStateRef.current = true;
  columnApi.setColumnDefs(isQuickEdit ? quickColumnDefs : normalColumnDefs);

  // Apply current user sort (if any)
  applySortModel(sortModelRef.current);

  gridRef.current?.api?.sizeColumnsToFit();
  setTimeout(() => {
    isApplyingColumnStateRef.current = false;
    refreshHasColumnChanges();
  }, 0);
}, [isQuickEdit, refreshHasColumnChanges, applySortModel, normalColumnDefs, quickColumnDefs]);

useEffect(() => {
  if (!gridReady || !sortPersistenceKey) return;
  let stored = null;
  try {
    stored = sessionStorage.getItem(sortPersistenceKey);
  } catch {
    stored = null;
  }
  if (!stored) return;
  try {
    const model = JSON.parse(stored);
    if (Array.isArray(model) && model.length > 0) {
      applySortModel(model);
      const t1 = setTimeout(() => applySortModel(model), 200);
      const t2 = setTimeout(() => applySortModel(model), 800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  } catch {
  }
}, [inventory, gridReady, sortPersistenceKey, applySortModel]);

  return {
    // refs
    gridRef,
    initialColumnState,
    quickEditColumnState,
    // state
    pageRows,
    selectedCount,
    sortModel,
    hasColumnChanges,
    // handlers
    onGridReady,
    onFirstDataRendered,
    handleSearchChange,
    handleSelectionChanged,
    handleRowClicked,
    handleSortChanged,
    handleColumnStateChange: refreshHasColumnChanges,
    clearSortModel,
    resetColumns,
    handleCellEditingStopped,
    context: {
      onThumbnailClick: (row) => onThumbnailClick?.(row),
      onViewChangeHistory: (row) => onViewChangeHistory?.(row),
    },
    columnDefs,
  };
}
